// Session-by-id hub API fns — carved verbatim from `src/api/scribeAPI.ts`
// (v1), `src/api/scribeAPIV2.ts`, and the inline permissions GET in
// `src/hooks/useSessionPermissionCheck.ts` (all of which now consume this
// module) with the D3 family. Transport comes from the `@heidi/api`
// configured-client registry; request paths, params, and bodies are
// byte-identical.

import { getScribeHttpClients } from '@heidi/api/client';
import { type components } from '@heidi/schema/ml-scribe';

import {
  type BulkCreateSessionParams,
  type SessionPermissionsResponse,
  type SessionResponse,
  type SessionUpdate,
  type SessionUpdateResponse,
  type UpdateTeamMemberSessionRequest,
} from '../../contracts';

type CreateSessionRequest =
  components['schemas']['domains__sessions__schemas__CreateSessionRequest'];

export const getSessionById = (sessionId: string, signal?: AbortSignal) => {
  return getScribeHttpClients().scribeHttp.get<SessionResponse>(
    `/sessions/${sessionId}`,
    { signal }
  );
};

// Carved with the warm pair (ARCFE-991) — `useMutateUpdateTranscriptFeedback`
// moved and a package hook cannot import the app hub. Keeps the original
// empty-id early return; callers may now pass an AbortSignal for cancellable
// background replays.
export const updateSessionById = (
  sessionId: string,
  sessionData: SessionUpdate,
  signal?: AbortSignal
) => {
  if (!sessionId) {
    return Promise.resolve({ data: null });
  }

  return getScribeHttpClients().scribeHttp.patch<SessionUpdateResponse>(
    `/sessions/${sessionId}`,
    sessionData,
    { signal }
  );
};

export const getTeamMemberSessionById = ({
  teamId,
  memberId,
  sessionId,
  signal,
}: {
  teamId: string;
  memberId: string;
  sessionId: string;
  signal?: AbortSignal;
}) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionResponse>(
    `/teams/${teamId}/members/${memberId}/sessions/${sessionId}`,
    { signal }
  );
};

/**
 * `GET /sessions/{id}/permissions` — the FGA session-permissions read,
 * extracted from `useSessionPermissionCheck`'s inline `scribeHttpV2.get`
 * (the hook stays app-side; only the transport moved). Path is
 * byte-identical, including its original relative (no leading slash) form.
 */
export const getSessionPermissions = (sessionId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<SessionPermissionsResponse>(
    `sessions/${sessionId}/permissions`
  );
};

export const postCreateSessionV2 = ({
  timezone,
  is_auto_mode,
  creation_platform,
  signal,
}: CreateSessionRequest & {
  is_auto_mode?: boolean;
  signal?: AbortSignal;
}) => {
  return getScribeHttpClients().scribeHttpV2.post<SessionResponse>(
    '/sessions/',
    {
      timezone,
      is_auto_mode,
      creation_platform,
    },
    { signal }
  );
};

export const postBulkCreateSessions = (sessions: BulkCreateSessionParams[]) => {
  return getScribeHttpClients().scribeHttpV2.post<SessionResponse[]>(
    '/sessions/batch',
    sessions
  );
};

export const postBulkCreateSessionsForTeamMember = ({
  teamId,
  memberId,
  sessions,
}: {
  teamId: string;
  memberId: string;
  sessions: BulkCreateSessionParams[];
}) => {
  return getScribeHttpClients().scribeHttpV2.post<SessionResponse[]>(
    `teams/${teamId}/members/${memberId}/sessions/batch`,
    sessions
  );
};

/**
 * `PATCH /teams/{t}/members/{m}/sessions/{s}` — the team-member session write,
 * carved verbatim from `src/api/scribeAPIV2.ts` (which now consumes this
 * module) with the session-header modal cores. The `@domain session`
 * tag retires with the carve. Path, verb and body are byte-identical, including
 * the original relative (no leading slash) form.
 */
export const updateSessionForOtherUser = ({
  teamId,
  memberId,
  sessionId,
  input,
}: {
  teamId: string;
  memberId: string;
  sessionId: string;
  input: UpdateTeamMemberSessionRequest;
}) => {
  return getScribeHttpClients().scribeHttpV2.patch<SessionResponse>(
    `teams/${teamId}/members/${memberId}/sessions/${sessionId}`,
    input
  );
};

type ScheduleUploadView = components['schemas']['ScheduleUploadView'];

/** `POST /sessions/upload-schedule` — multipart schedule file. */
export const postUploadSessionSchedule = (file: File, signal?: AbortSignal) => {
  const formData = new FormData();
  formData.append('file', file);
  return getScribeHttpClients().scribeHttpV2.post<ScheduleUploadView>(
    '/sessions/upload-schedule',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal,
    }
  );
};
